// SPDX-License-Identifier: MIT
/** Typography helpers shared by scenes: per-glyph layout and animation for Latin and CJK text. */
export type GlyphState = {
    dx?: number;
    dy?: number;
    alpha?: number;
    scale?: number;
};
/** Split into user-perceived characters (code points are enough for Latin + CJK display copy). */
export const chars = (text: string): string[] => Array.from(text);
export function measure(ctx: CanvasRenderingContext2D, text: string): number {
    return ctx.measureText(text).width;
}
/**
 * Draw `text` glyph by glyph so each glyph can animate independently. Uses the current ctx.font,
 * fillStyle and letterSpacing. Returns the laid-out width.
 */
export function drawGlyphs(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, align: 'left' | 'center' | 'right', state: (index: number, count: number) => GlyphState): number {
    const list = chars(text);
    const widths = list.map((c) => ctx.measureText(c).width);
    const spacing = parseFloat(ctx.letterSpacing) || 0;
    const total = widths.reduce((a, b) => a + b, 0) + spacing * Math.max(0, list.length - 1);
    let cursor = align === 'left' ? x : align === 'center' ? x - total / 2 : x - total;
    const prevAlign = ctx.textAlign;
    const prevAlpha = ctx.globalAlpha;
    ctx.textAlign = 'left';
    list.forEach((c, i) => {
        const s = state(i, list.length);
        const alpha = s.alpha ?? 1;
        if (alpha > 0.002) {
            const w = widths[i]!;
            const scale = s.scale ?? 1;
            ctx.globalAlpha = prevAlpha * alpha;
            if (scale !== 1) {
                ctx.save();
                ctx.translate(cursor + w / 2 + (s.dx ?? 0), y + (s.dy ?? 0));
                ctx.scale(scale, scale);
                ctx.fillText(c, -w / 2, 0);
                ctx.restore();
            }
            else {
                ctx.fillText(c, cursor + (s.dx ?? 0), y + (s.dy ?? 0));
            }
        }
        cursor += widths[i]! + spacing;
    });
    ctx.textAlign = prevAlign;
    ctx.globalAlpha = prevAlpha;
    return total;
}
export function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
}
